import { Dropdown, DropdownItem, DropdownList } from '@cokitchen/cokitchen-components';
import { ArrowDownIcon, CloseOutlineIcon } from 'cokitchen-icons';
import React, { useMemo, useState, ReactNode, Fragment, useEffect, useCallback } from 'react';

const SelectDropdown = ({
  options,
  value,
  placeholder,
  setValue,
  label,
  hasSearch,
  subAction,
  disabled,
  loading_options,
  price_value,
  onChange
}: {
  options: { id: any; name: string; price?: string }[];
  value: string | undefined;
  placeholder: string;
  setValue: (arg0: any) => void;
  label?: string;
  hasSearch?: boolean;
  subAction?: ReactNode;
  disabled?: boolean;
  loading_options?: boolean;
  price_value?: string;
  onChange?: (val: string) => void;
}) => {
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);

  const filteredOptions = useMemo(() => {
    if (!hasSearch || !search) return options;
    return options?.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()));
  }, [options, search]);

  useEffect(() => {
    if (hasSearch && value) {
      setSearch(value);
    }
  }, [value]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % filteredOptions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev === 0 ? filteredOptions.length - 1 : prev - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selectedOption = filteredOptions[highlightedIndex];
        if (selectedOption) {
          setValue(selectedOption);
          setSearch(selectedOption.name);
          setShow(false);
        }
      }
    },
    [filteredOptions, highlightedIndex, setValue]
  );

  const clearSearch = () => {
    setSearch('');
    onChange?.('');
  };

  return (
    <Dropdown
      disabled={disabled}
      visible={show}
      onClickOutside={() => setShow(false)}
      className='w-full'
      content={
        <div onClick={() => setShow(!show)}>
          <p className='mb-2 font-medium'>{label}</p>
          <div className='border-b-2 border-b-[#D5D6D7] bg-[#FAFAFA] rounded-none h-[46px] text-sm flex items-center px-4 justify-between cursor-pointer'>
            {value ? (
              <div className='flex gap-1 items-center'>
                <p className='text-base font-medium capitalize flex gap-2 items-center'>{value}</p>
                {price_value && (
                  <Fragment>
                    <div className='bg-[#DCDBD5] w-[5px] h-[5px] rounded-full' />
                    <p className='text-base font-medium capitalize flex gap-2 items-center'>
                      {price_value}
                    </p>
                  </Fragment>
                )}
              </div>
            ) : (
              <span className='text-base text-gray-4 font-medium'>{placeholder}</span>
            )}
            <div className='text-gray-4'>
              <ArrowDownIcon />
            </div>
          </div>
          {subAction}
        </div>
      }>
      <DropdownList>
        {hasSearch && (
          <div className='sticky top-0 bg-white z-10 flex items-center relative'>
            <input
              value={search}
              onChange={(e) => {
                onChange?.(e.target.value);
                setSearch(e.target.value);
              }}
              className='w-full bg-transparent outline-none text-base font-medium py-2 px-4 border-b border-[#d5d6d7]'
              placeholder={`${placeholder || 'Search'}`}
              aria-label='Search'
              autoFocus
            />
            {search && (
              <button
                onClick={clearSearch}
                className='absolute right-4 text-gray-500 hover:text-gray-800'
                aria-label='Clear search input'>
                <CloseOutlineIcon />
              </button>
            )}
          </div>
        )}
        {filteredOptions.length === 0 && !loading_options ? (
          <div className='px-4 py-2 text-sm text-[#d5d6d7]'>Item not found</div>
        ) : (
          <div className='max-h-60 ' onKeyDown={handleKeyDown}>
            {filteredOptions.map((option, index) => (
              <DropdownItem
                key={index}
                onClick={() => {
                  setValue(option);
                  onChange?.('');
                  setSearch('');
                  setShow(false);
                }}
                className={`cursor-pointer ${highlightedIndex === index ? 'bg-gray-200' : ''}`}
                aria-selected={highlightedIndex === index}>
                <p className='capitalize'>{option.name}</p>
                {option.price && (
                  <div className='flex items-center gap-1 ml-1'>
                    <div className='bg-[#DCDBD5] w-[5px] h-[5px] rounded-full' />
                    <p className='capitalize'>{option.price}</p>
                  </div>
                )}
              </DropdownItem>
            ))}
            {loading_options && (
              <DropdownItem>
                <p className='text-xs'>Loading options...</p>
              </DropdownItem>
            )}
          </div>
        )}
      </DropdownList>
    </Dropdown>
  );
};

export default SelectDropdown;
